import React from 'react';
import styled from 'styled-components';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { FeedbackStatus } from 'donor/portal/pages/FeedbackDashboard/api';
import { UserType } from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';

interface FeedbackHeaderProps {
  userType: UserType;
  totalFeedbacks: number;
  feedbackStatus: FeedbackStatus;
  numberOfCompletedFeedback: number;
  handleDone: () => unknown;
  handleStart: () => unknown;
}

const getText = (
  status: FeedbackStatus,
  userType: UserType,
  total: number,
  completed: number,
  handleStart: FeedbackHeaderProps['handleStart'],
  handleDone: FeedbackHeaderProps['handleDone'],
) => {
  const done = total === completed;
  switch (status) {
    case 'Awaiting TI Feedback':
    case 'Awaiting Funder Feedback':
      return {
        heading: 'The report is ready for your review and feedbacks',
        subheading: (
          <>
            Please take a moment to review all the questions and their answers.
            You can either leave a overall section feedback or a review on
            specific question.
          </>
        ),
        button:
          total === completed
            ? {
                text: 'Submit Feedback',
                onClick: handleDone,
              }
            : {
                text: 'Begin Review',
                onClick: handleStart,
              },
      };

    default:
      return {
        heading: 'Your report is ready to review',
        subheading: (
          <>
            True Impact and your donor left you{' '}
            <strong>
              {total === 1
                ? '1 feedback comment'
                : `${total} feedback comments`}
            </strong>
            . You can see the overview of items below. Please review them as
            soon as possible so we can finalize your report.
          </>
        ),
        button: done
          ? {
              text: 'Submit Review',
              onClick: handleDone,
            }
          : {
              text: 'Begin Review',
              onClick: handleStart,
            },
      };
  }
};

const StyledDiv = styled.div`
  border-bottom: 1px solid ${COLORS.grayLight};
  .overview-top-section-container {
    max-width: 478px;
    text-align: center;
    margin-left: auto;
    margin-right: auto;
    .overview-section-text {
      margin-top: 27px;
      margin-bottom: ${SPACERS.medium};
    }
    .overview-button {
      margin-left: auto;
      margin-right: auto;
      margin-bottom: ${SPACERS.extraLarge};
    }
  }
`;

const FeedbackHeader: React.FC<FeedbackHeaderProps> = ({
  userType,
  totalFeedbacks,
  feedbackStatus,
  numberOfCompletedFeedback,
  handleStart,
  handleDone,
}) => {
  const text = getText(
    feedbackStatus,
    userType,
    totalFeedbacks,
    numberOfCompletedFeedback,
    handleStart,
    handleDone,
  );
  return (
    <StyledDiv>
      <div className="overview-top-section-container">
        <TrueImpactText variant="heading" className="overview-section-heading">
          {text.heading}
        </TrueImpactText>
        <TrueImpactText className="overview-section-text">
          {text.subheading}
        </TrueImpactText>
        <TrueImpactButton
          onClick={text.button.onClick}
          className="overview-button"
        >
          {text.button.text}
        </TrueImpactButton>
      </div>
    </StyledDiv>
  );
};

export default FeedbackHeader;
